import React, { useState, useEffect } from 'react';
import TreeMap from '../components/TreeMap';
import PriceDisplay from '../components/PriceDisplay';
import UniversalNavbar from '../components/UniversalNavbar';
import { useCurrency } from '../hooks/useCurrency';
import PhotoPackagePaymentForm from '../components/PhotoPackagePaymentForm';

interface Order {
  _id: string;
  trees: number;
  treesOrdered?: number;
  totalAmount: number;
  amount?: number;
  currency: string;
  status: 'pending' | 'assigned' | 'planted' | 'completed';
  createdAt: string;
  plantingDate?: string;
  photoDate?: string;
  assignedWorker?: {
    name: string;
    email: string;
  };
  location?: string | { description?: string; region?: string; site?: string };
  coordinates?: { latitude: number; longitude: number; };
  photos?: string[] | Array<{ filename: string; date?: string; timestamp?: string; }>;
}

interface CustomerStats {
  totalTrees: number;
  totalOrders: number;
  treesPlanted: number;
  carbonOffset: number;
}

interface PhotoPackageSchedule {
  scheduledDate: string;
  description: string;
  status: 'pending' | 'assigned' | 'completed' | 'overdue';
  completedDate?: string;
  notes?: string;
  photoUrl: string | null;
}

interface PhotoPackageInfo {
  id: string;
  packageType: string;
  packageName: string;
  duration: string;
  status: string;
  totalPhotoCount: number;
  purchaseDate: string;
  expiryDate?: string;
  photosCompleted: number;
  schedule: PhotoPackageSchedule[];
}

const CustomerPortal: React.FC = () => {
  const { calculatePrice } = useCurrency();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Smart-auth form state — one email + one password field for everyone.
  // "name" is collected but only used server-side if we end up creating a new
  // customer record (case 3 in smart-auth).
  const [smartData, setSmartData] = useState({ email: '', password: '', name: '' });
  const [authError, setAuthError] = useState<string | null>(null);
  const [customerInfo, setCustomerInfo] = useState<{ name: string; email: string } | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [photoPackages, setPhotoPackages] = useState<PhotoPackageInfo[]>([]);
  const [stats, setStats] = useState<CustomerStats>({ totalTrees: 0, totalOrders: 0, treesPlanted: 0, carbonOffset: 0 });
  const [loading, setLoading] = useState(false);
  const [showPhotoPackages, setShowPhotoPackages] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState<string | null>(null); // Track which order is showing payment form
  const [selectedPackage, setSelectedPackage] = useState<{
    priceId: string;
    orderId: string;
    packageName: string;
    packagePrice: number;
  } | null>(null);

  useEffect(() => {
    // Check if already authenticated
    const token = localStorage.getItem('customerToken');
    if (token) {
      verifyToken(token);
    }

    // Deep-link from welcome email: ?mode=setup&email=foo@bar.com
    // We no longer show different forms per mode (smart-auth handles all 3),
    // but we still pre-fill the email from the link so the user just types a
    // password and continues.
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setSmartData(prev => ({ ...prev, email: emailParam }));
    }
  }, []);

  const verifyToken = async (token: string) => {
    try {
      const response = await fetch('/api/auth/customer/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setCustomerInfo(data.user);
        loadCustomerData();
      } else {
        localStorage.removeItem('customerToken');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('customerToken');
    }
  };

  // Single auth handler — backend decides login vs setup vs register.
  const handleSmartAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    if (smartData.password.length < 6) {
      setAuthError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/auth/customer/smart-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: smartData.email,
          password: smartData.password,
          name: smartData.name || undefined,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('customerToken', data.token);
        setIsAuthenticated(true);
        setCustomerInfo(data.user);
        loadCustomerData();
      } else {
        setAuthError(data.error || 'No pudimos iniciar sesión. Intenta de nuevo.');
      }
    } catch (error) {
      console.error('Smart auth error:', error);
      setAuthError('No pudimos conectar con el servidor. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Legacy /customer/login, /customer/register, /customer/setup-password
  // handlers were removed when we collapsed the 3-tab UI into smart-auth.
  // The backend endpoints still exist (see backend/routes/auth.js) for any
  // external integrations, but this portal only uses /smart-auth now.

  const handlePhotoPackagePurchase = (priceId: string, originalOrderId: string, packageName: string, packagePrice: number) => {
    setSelectedPackage({
      priceId,
      orderId: originalOrderId,
      packageName,
      packagePrice
    });
    setShowPaymentForm(originalOrderId);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(null);
    setSelectedPackage(null);
    setShowPhotoPackages(null);
    loadCustomerData();
    alert('Payment successful! Your photo package has been activated.');
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(null);
    setSelectedPackage(null);
  };

  const loadCustomerData = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      
      // Load orders
      const ordersResponse = await fetch('/api/customers/orders', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (ordersResponse.ok) {
        const ordersData = await ordersResponse.json();
        setOrders(ordersData.orders || []);

        // Calculate stats (1 Order = 1 Tree; legacy orders may still carry a count)
        const totalTrees = ordersData.orders.reduce((sum: number, order: Order) => sum + (order.trees || order.treesOrdered || 1), 0);
        const treesPlanted = ordersData.orders
          .filter((order: Order) => order.status === 'planted' || order.status === 'completed')
          .reduce((sum: number, order: Order) => sum + (order.trees || order.treesOrdered || 1), 0);

        setStats({
          totalTrees,
          totalOrders: ordersData.orders.length,
          treesPlanted,
          carbonOffset: treesPlanted * 22 // Approximate kg CO2 per tree per year
        });
      }

      // Load photo packages (purchased growth-update subscriptions)
      const packagesResponse = await fetch('/api/customers/photo-packages', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (packagesResponse.ok) {
        const packagesData = await packagesResponse.json();
        setPhotoPackages(packagesData.photoPackages || []);
      }
    } catch (error) {
      console.error('Failed to load customer data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customerToken');
    setIsAuthenticated(false);
    setCustomerInfo(null);
    setOrders([]);
    setStats({ totalTrees: 0, totalOrders: 0, treesPlanted: 0, carbonOffset: 0 });
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'pending': { background: '#fff3cd', color: '#856404' },
      'assigned': { background: '#d4edda', color: '#155724' },
      'planted': { background: '#d1ecf1', color: '#0c5460' },
      'completed': { background: '#d1e7dd', color: '#0a3622' }
    };
    return colors[status as keyof typeof colors] || { background: '#e9ecef', color: '#495057' };
  };

  if (!isAuthenticated) {
    return (
      <div className="portal-container" style={{ marginTop: '100px' }}>
        <div className="login-form" style={{
          maxWidth: '420px',
          margin: '0 auto',
          background: 'white',
          padding: '2rem',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ textAlign: 'center', marginBottom: '0.25rem', color: '#333' }}>
            Acceso de Cliente
          </h2>
          <p style={{ textAlign: 'center', color: '#666', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            Ingresa tu correo y contraseña. Si ya compraste un árbol, usa el correo de tu compra.
          </p>

          <form onSubmit={handleSmartAuth}>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Correo</label>
              <input
                type="email"
                value={smartData.email}
                onChange={(e) => setSmartData({ ...smartData, email: e.target.value })}
                required
                autoComplete="email"
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem' }}
              />
            </div>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Contraseña</label>
              <input
                type="password"
                value={smartData.password}
                onChange={(e) => setSmartData({ ...smartData, password: e.target.value })}
                required
                minLength={6}
                autoComplete="current-password"
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem' }}
              />
              <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: '#888' }}>
                Mínimo 6 caracteres. Si es tu primera vez aquí, esta será tu contraseña.
              </p>
            </div>
            <div style={{ marginBottom: '1.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Nombre <span style={{ fontWeight: 400, color: '#888' }}>(opcional)</span>
              </label>
              <input
                type="text"
                value={smartData.name}
                onChange={(e) => setSmartData({ ...smartData, name: e.target.value })}
                autoComplete="name"
                placeholder="Solo si es tu primera vez"
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem' }}
              />
            </div>

            {authError && (
              <div style={{
                marginBottom: '1rem',
                padding: '10px 12px',
                background: '#fdecea',
                color: '#a12622',
                borderRadius: '8px',
                fontSize: '0.9rem',
                textAlign: 'center'
              }}>
                {authError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '12px',
                background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
                color: 'white', border: 'none', borderRadius: '8px',
                fontSize: '1rem', fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Procesando...' : 'Continuar'}
            </button>
          </form>

          {/* The old 3-tab UI is intentionally removed. Smart-auth on the backend
              handles new customers, imported customers without a password, and
              returning customers all from this one form. */}

          {/* Portal Links for Workers and Admins */}
          <div style={{
            textAlign: 'center',
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e0e0e0'
          }}>
            <p style={{
              fontSize: '0.85rem',
              color: '#666',
              marginBottom: '0.5rem'
            }}>
              {'Not a customer?'}
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              alignItems: 'center'
            }}>
              <a
                href="/worker-portal"
                style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#228B22'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
              >
                {'Worker Login'} →
              </a>
              <span style={{ color: '#ddd' }}>|</span>
              <a
                href="/admin-dashboard"
                style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#228B22'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
              >
                {'Admin Login'} →
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <UniversalNavbar 
        userType="customer"
        user={customerInfo || undefined}
        onLogout={handleLogout}
        
      />
      <div className="portal-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', marginTop: '100px' }}>
        {/* Dashboard Header */}
        <div style={{
          background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
          color: 'white',
          padding: '2rem',
          borderRadius: '15px',
          marginBottom: '2rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h1 style={{ margin: 0, marginBottom: '0.5rem' }}>
              {'Rastrea el impacto de tu plantación de árboles y ve crecer tu bosque'}
            </h1>
            {customerInfo && (
              <p style={{ margin: 0, opacity: 0.8, fontSize: '0.9rem', marginTop: '0.5rem' }}>
                {customerInfo.email}
              </p>
            )}
          </div>
        </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '10px',
          boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#228B22' }}>
            {stats.totalTrees}
          </div>
          <div style={{ color: '#666', marginTop: '0.5rem' }}>
            {'Árboles Pedidos'}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '10px',
          boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#228B22' }}>
            {stats.treesPlanted}
          </div>
          <div style={{ color: '#666', marginTop: '0.5rem' }}>
            {'Árboles Plantados'}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '10px',
          boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#228B22' }}>
            {stats.totalOrders}
          </div>
          <div style={{ color: '#666', marginTop: '0.5rem' }}>
            {'Órdenes'}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '10px',
          boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#228B22' }}>
            {Math.round(stats.carbonOffset)}
          </div>
          <div style={{ color: '#666', marginTop: '0.5rem' }}>
            {'kg CO₂ capturado/año'}
          </div>
        </div>
      </div>

      {/* Orders Section */}
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '15px',
        boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333', marginBottom: '1.5rem' }}>
          {'Mis Órdenes de Árboles'}
        </h2>

        {orders.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
            <p>{'No hay órdenes aún. ¡Comienza a plantar árboles!'}</p>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                marginTop: '1rem',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              {'Plantar Árboles'}
            </button>
          </div>
        ) : (
          orders.map((order, index) => (
            <div
              key={order._id}
              style={{
                border: '1px solid #eee',
                padding: '1.5rem',
                marginBottom: '1rem',
                borderRadius: '10px',
                transition: 'box-shadow 0.3s ease'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>
                  {'Orden #'}{order._id.slice(-6)}
                </span>
                <span
                  style={{
                    padding: '5px 15px',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    ...getStatusColor(order.status)
                  }}
                >
                  {({'pending':'Pendiente','assigned':'Asignado','planted':'Plantado','completed':'Completado'} as Record<string,string>)[order.status] || order.status}
                </span>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div>
                  <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                    {'Árboles'}
                  </div>
                  <div style={{ color: '#333', marginTop: '0.2rem' }}>
                    {order.trees || order.treesOrdered || 1} {(order.trees || order.treesOrdered || 1) === 1 ? 'árbol' : 'árboles'}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                    {'Cantidad Pagada'}
                  </div>
                  <div style={{ color: '#333', marginTop: '0.2rem' }}>
                    {order.currency || 'USD'} {((order.totalAmount || order.amount || 0) / 100).toFixed(2)}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                    {'Fecha de Orden'}
                  </div>
                  <div style={{ color: '#333', marginTop: '0.2rem' }}>
                    {new Date(order.createdAt).toLocaleDateString()}
                  </div>
                </div>

                {order.assignedWorker && (
                  <div>
                    <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                      {'Plantador de Árboles'}
                    </div>
                    <div style={{ color: '#333', marginTop: '0.2rem' }}>
                      {order.assignedWorker.name}
                    </div>
                  </div>
                )}
              </div>


              {order.coordinates && (
                <TreeMap
                  latitude={order.coordinates.latitude}
                  longitude={order.coordinates.longitude}
                  location={
                    typeof order.location === 'string'
                      ? order.location
                      : order.location?.description
                  }
                  treeNumber={index + 1}
                />
              )}

              {order.photos && order.photos.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                    {'Fotos del Árbol'}
                  </div>
                  <div style={{ 
                    display: order.photos?.length === 1 ? 'block' : 'grid',
                    gridTemplateColumns: order.photos?.length === 2 ? 'repeat(2, 1fr)' : 
                                       (order.photos?.length || 0) <= 4 ? 'repeat(auto-fit, minmax(250px, 1fr))' : 
                                       'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    maxWidth: '100%',
                    alignItems: 'start'
                  }}>
                    {order.photos.map((photo, index) => {
                      // Handle both string and object photo formats
                      const photoFilename = typeof photo === 'string' ? photo : (photo as any).filename || photo;
                      const photoDate = typeof photo === 'object' && photo !== null ? 
                                       (photo as any).date || (photo as any).timestamp : null;
                      
                      // Use photo date, planting date, or order date as fallback
                      const displayDate = photoDate || order.plantingDate || order.photoDate || order.createdAt;
                      const formattedDate = new Date(displayDate).toLocaleDateString();
                      
                      return (
                        <div
                          key={index}
                          style={{
                            position: 'relative',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#f8f9fa'
                          }}
                          onClick={() => window.open(`/api/photos/${photoFilename}`, '_blank')}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                          }}
                        >
                          <img
                            src={`/api/photos/${photoFilename}`}
                            alt={`Tree ${index + 1}`}
                            style={{
                              width: '100%',
                              height: order.photos?.length === 1 ? 'auto' : '300px',
                              display: 'block',
                              objectFit: 'contain',
                              objectPosition: 'center',
                              maxHeight: order.photos?.length === 1 ? '500px' : '300px',
                              backgroundColor: '#f8f9fa'
                            }}
                            onError={(e) => {
                              console.error('Failed to load photo:', photoFilename);
                              e.currentTarget.parentElement!.style.display = 'none';
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            bottom: '8px',
                            left: '8px',
                            right: '8px',
                            background: 'rgba(0,0,0,0.8)',
                            color: 'white',
                            padding: '6px 10px',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}>
                            <div style={{ marginBottom: '2px' }}>
                              {'Árbol'} {index + 1}
                            </div>
                            <div style={{ fontSize: '0.7rem', opacity: '0.9' }}>
                              {formattedDate}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Photo Package Button */}
                  <div style={{ 
                    textAlign: 'center', 
                    marginTop: '1.5rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <button
                      onClick={() => setShowPhotoPackages(showPhotoPackages === order._id ? null : order._id)}
                      style={{
                        background: showPhotoPackages === order._id ? 
                          'linear-gradient(135deg, #1e7a1e, #28a428)' :
                          'linear-gradient(135deg, #228B22, #32CD32)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '25px',
                        padding: '12px 24px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(34, 139, 34, 0.3)',
                        transition: 'all 0.3s ease',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(34, 139, 34, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 139, 34, 0.3)';
                      }}
                    >
                      {showPhotoPackages === order._id ? '✕' : '📸'} {' '}
                      {showPhotoPackages === order._id ? 
                        'Close' : 
                        'Obtener Actualizaciones de Crecimiento'}
                    </button>
                  </div>
                  
                  {/* Inline Photo Packages */}
                  {showPhotoPackages === order._id && (
                    <div style={{
                      marginTop: '1.5rem',
                      padding: '2rem',
                      background: '#f8f9fa',
                      borderRadius: '12px',
                      border: '1px solid #e9ecef',
                      animation: 'slideDown 0.3s ease-out'
                    }}>
                      <div style={{
                        textAlign: 'center',
                        marginBottom: '1.5rem'
                      }}>
                        <h3 style={{ margin: 0, color: '#228B22', marginBottom: '0.5rem' }}>
                          📸 {'Mira Crecer Tu Árbol'}
                        </h3>
                        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                          {'Obtén actualizaciones fotográficas profesionales mostrando el crecimiento de tu árbol a lo largo del tiempo. Todas las fotos son tomadas por nuestros trabajadores de campo durante rondas fotográficas estacionales.'}
                        </p>
                      </div>

                      <div style={{
                        display: 'grid',
                        gap: '1rem',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))'
                      }} className="customer-photo-package-grid">
                        {/* First Year Journey */}
                        <div style={{
                          border: '2px solid #e0e0e0',
                          borderRadius: '12px',
                          padding: '1.5rem',
                          textAlign: 'center',
                          position: 'relative',
                          background: 'white'
                        }} className="customer-photo-package-card">
                          <h4 style={{ color: '#228B22', fontSize: '1.1rem', marginBottom: '0.5rem' }} className="customer-photo-package-title">
                            {'Recorrido del Primer Año'}
                          </h4>
                          <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#228B22', marginBottom: '0.5rem' }} className="customer-photo-package-price">
                            <PriceDisplay basePrice={15} />
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                            {'3 actualizaciones fotográficas'}<br/>
                            <span style={{ fontSize: '0.75rem' }}>({calculatePrice(5)} {'por actualización'})</span>
                          </div>
                          <ul style={{
                            textAlign: 'left',
                            fontSize: '0.75rem',
                            color: '#666',
                            marginBottom: '1rem',
                            listStyle: 'none',
                            padding: 0
                          }}>
                            <li>✓ {'Primera foto a los 3 meses'}</li>
                            <li>✓ {'Segunda foto a los 6 meses'}</li>
                            <li>✓ {'Tercera foto al año'}</li>
                          </ul>
                          <button
                            onClick={() => handlePhotoPackagePurchase('price_1RuZnCDPhbsxRB8Hk72EfX23', order._id, 'First Year Journey', 15)}
                            style={{
                              width: '100%',
                              background: 'linear-gradient(135deg, #228B22, #32CD32)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '10px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            {'Elegir Plan'}
                          </button>
                        </div>

                        {/* Three Year Story */}
                        <div style={{
                          border: '3px solid #228B22',
                          borderRadius: '12px',
                          padding: '1.5rem',
                          textAlign: 'center',
                          position: 'relative',
                          background: 'linear-gradient(135deg, #f8fff8, #ffffff)'
                        }} className="customer-photo-package-card">
                          <div style={{
                            position: 'absolute',
                            top: '-10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: '#228B22',
                            color: 'white',
                            padding: '4px 16px',
                            borderRadius: '20px',
                            fontSize: '0.7rem',
                            fontWeight: '600'
                          }}>
                            {'POPULAR'}
                          </div>
                          <h4 style={{ color: '#228B22', fontSize: '1.1rem', marginBottom: '0.5rem' }} className="customer-photo-package-title">
                            {'Historia de Tres Años'}
                          </h4>
                          <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#228B22', marginBottom: '0.5rem' }} className="customer-photo-package-price">
                            <PriceDisplay basePrice={25} />
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                            {'7 actualizaciones fotográficas'}<br/>
                            <span style={{ fontSize: '0.75rem', color: '#228B22', fontWeight: '600' }}>({calculatePrice(3.57)} {'por actualización'}) {'¡Ahorra 29%!'}</span>
                          </div>
                          <ul style={{
                            textAlign: 'left',
                            fontSize: '0.75rem',
                            color: '#666',
                            marginBottom: '1rem',
                            listStyle: 'none',
                            padding: 0
                          }}>
                            <li>✓ {'Cada 6 meses durante 3 años'}</li>
                            <li>✓ {'Historia completa de crecimiento'}</li>
                            <li>✓ {'Fotos profesionales estacionales'}</li>
                          </ul>
                          <button
                            onClick={() => handlePhotoPackagePurchase('price_1RuZnCDPhbsxRB8HuGxLgYgn', order._id, 'Three Year Story', 25)}
                            style={{
                              width: '100%',
                              background: 'linear-gradient(135deg, #228B22, #32CD32)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(34, 139, 34, 0.3)',
                              fontSize: '0.85rem'
                            }}
                          >
                            {'Elegir Plan'}
                          </button>
                        </div>

                        {/* Lifetime Growth */}
                        <div style={{
                          border: '2px solid #e0e0e0',
                          borderRadius: '12px',
                          padding: '1.5rem',
                          textAlign: 'center',
                          position: 'relative',
                          background: 'white'
                        }} className="customer-photo-package-card">
                          <h4 style={{ color: '#228B22', fontSize: '1.1rem', marginBottom: '0.5rem' }} className="customer-photo-package-title">
                            {'Crecimiento de Por Vida'}
                          </h4>
                          <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#228B22', marginBottom: '0.5rem' }} className="customer-photo-package-price">
                            <PriceDisplay basePrice={35} />
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                            {'10 actualizaciones fotográficas'}<br/>
                            <span style={{ fontSize: '0.75rem', color: '#228B22', fontWeight: '600' }}>({calculatePrice(3.5)} {'por actualización'}) {'¡Ahorra 30%!'}</span>
                          </div>
                          <ul style={{
                            textAlign: 'left',
                            fontSize: '0.75rem',
                            color: '#666',
                            marginBottom: '1rem',
                            listStyle: 'none',
                            padding: 0
                          }}>
                            <li>✓ {'Recorrido completo de 5 años'}</li>
                            <li>✓ {'Mejor valor por foto'}</li>
                            <li>✓ {'Documentación completa de maduración'}</li>
                          </ul>
                          <button
                            onClick={() => handlePhotoPackagePurchase('price_1RuZnDDPhbsxRB8HvIY1kAjF', order._id, 'Lifetime Growth', 35)}
                            style={{
                              width: '100%',
                              background: 'linear-gradient(135deg, #228B22, #32CD32)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '10px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            {'Elegir Plan'}
                          </button>
                        </div>
                      </div>

                      {/* Inline Payment Form */}
                      {showPaymentForm === order._id && selectedPackage && selectedPackage.orderId === order._id && (
                        <div style={{
                          marginTop: '1.5rem',
                          padding: '2rem',
                          background: 'white',
                          borderRadius: '12px',
                          border: '2px solid #228B22',
                          animation: 'slideDown 0.3s ease-out'
                        }}>
                          <PhotoPackagePaymentForm
                            priceId={selectedPackage.priceId}
                            originalOrderId={selectedPackage.orderId}
                            packageName={selectedPackage.packageName}
                            packagePrice={selectedPackage.packagePrice}
                            onSuccess={handlePaymentSuccess}
                            onCancel={handlePaymentCancel}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Photo Packages Section — the growth-update packages the customer bought */}
      {photoPackages.length > 0 && (
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
          marginBottom: '2rem'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333', marginBottom: '1.5rem' }}>
            📸 {'Mis Paquetes de Fotos'}
          </h2>

          {photoPackages.map(pkg => (
            <div
              key={pkg.id}
              style={{
                border: '1px solid #eee',
                padding: '1.5rem',
                marginBottom: '1rem',
                borderRadius: '10px'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem',
                marginBottom: '1rem'
              }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>
                  {pkg.packageName || pkg.packageType}
                </span>
                <span style={{
                  padding: '5px 15px',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  ...(pkg.status === 'active' ? { background: '#d4edda', color: '#155724' }
                    : pkg.status === 'completed' ? { background: '#d1e7dd', color: '#0a3622' }
                    : pkg.status === 'pending_verification' ? { background: '#fff3cd', color: '#856404' }
                    : { background: '#e9ecef', color: '#495057' })
                }}>
                  {({
                    'active': 'Activo',
                    'completed': 'Completado',
                    'pending_verification': 'Pago en verificación',
                    'payment_rejected': 'Pago rechazado',
                    'cancelled': 'Cancelado'
                  } as Record<string, string>)[pkg.status] || pkg.status}
                </span>
              </div>

              <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                {pkg.photosCompleted} {'de'} {pkg.totalPhotoCount} {'fotos entregadas'}
              </div>

              {pkg.status === 'pending_verification' ? (
                <div style={{ color: '#856404', fontSize: '0.9rem' }}>
                  {'Tu pago está siendo verificado (24-48 horas). El cronograma de fotos aparecerá aquí cuando se confirme.'}
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {pkg.schedule.map((photo, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'center',
                        padding: '0.75rem',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        flexWrap: 'wrap'
                      }}
                    >
                      {photo.photoUrl ? (
                        <img
                          src={photo.photoUrl}
                          alt={photo.description}
                          onClick={() => window.open(photo.photoUrl!, '_blank')}
                          style={{
                            width: '90px',
                            height: '90px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '90px',
                          height: '90px',
                          borderRadius: '8px',
                          background: '#e9ecef',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.5rem',
                          flexShrink: 0
                        }}>
                          📷
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: '180px' }}>
                        <div style={{ fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>
                          {photo.description}
                        </div>
                        <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.2rem' }}>
                          {photo.status === 'completed' && photo.completedDate
                            ? `${'Tomada el'} ${new Date(photo.completedDate).toLocaleDateString()}`
                            : `${'Programada para'} ${new Date(photo.scheduledDate).toLocaleDateString()}`}
                        </div>
                        {photo.notes && (
                          <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.2rem', fontStyle: 'italic' }}>
                            {photo.notes}
                          </div>
                        )}
                      </div>
                      <span style={{
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        ...(photo.status === 'completed' ? { background: '#d4edda', color: '#155724' }
                          : photo.status === 'overdue' ? { background: '#f8d7da', color: '#721c24' }
                          : { background: '#fff3cd', color: '#856404' })
                      }}>
                        {({
                          'completed': 'Lista',
                          'pending': 'Pendiente',
                          'assigned': 'En camino',
                          'overdue': 'Retrasada'
                        } as Record<string, string>)[photo.status] || photo.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      </div>
    </>
  );
};

export default CustomerPortal;
